import { queryOptions } from '@tanstack/react-query'
import { apiGet } from './client'

/**
 * Settings -> Reports, filele pe module (Inventoree, Printeeng, Securitee,
 * Productivitee, Proactivitee), calea noua.
 *
 * Perechile vechi, toate din `settings/Company_reports`:
 *
 *   <modul>_report / <modul>_settings_report -> GET /settings/company-reports/{modul}
 *   get<Modul>Summary                         -> GET .../{modul}/summary?filterFrom&filterTo
 *   get<...>Data (tabelele)                   -> GET .../{modul}/tables/{tabel}?...
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`.
 *
 * Sumarul vine ca CIFRE (cheile din `summaryCounters()` ale modelului vechi),
 * nu ca HTML gata desenat; cardurile le scrie pagina. Tabelele raspund in
 * forma DataTables, cu numele de coloane ale modelelor vechi.
 */

export const REPORT_MODULES = ['inventoree', 'printeeng', 'securitee', 'productivitee', 'proactivitee'] as const

export type ReportModule = (typeof REPORT_MODULES)[number]

export function isReportModule(value: string): value is ReportModule {
  return (REPORT_MODULES as readonly string[]).includes(value)
}

export type ModuleReportShell = {
  module: string
  title: string
  moduleUrl: string
  /** Ziua de azi pe server (`Y-m-d`); macheta pornea cu ea la ambele capete. */
  today: string
  /** Poarta machetei (`enableReporting` + `view_report`); `false` = "Access denied". */
  allowed: boolean
  tables: string[]
}

export type ModuleReportSummary = {
  module: string
  from: string
  to: string
  counters: Record<string, number | string | null>
}

export type ModuleReportTableRequest = {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderDir: 'asc' | 'desc'
  search: string
  filterFrom: string
  filterTo: string
  /** Numai `proactivitee/opened-alarms` (0..3). */
  severity?: number
}

export type ModuleReportTablePage = {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: Record<string, unknown>[]
}

export function moduleReportShellQuery(module: ReportModule) {
  return queryOptions({
    queryKey: ['settings', 'company-reports', module, 'shell'],
    queryFn: () => apiGet<ModuleReportShell>(`/settings/company-reports/${module}`),
  })
}

export function moduleReportSummaryQuery(module: ReportModule, from: string, to: string) {
  const p = new URLSearchParams({ filterFrom: from, filterTo: to })

  return queryOptions({
    queryKey: ['settings', 'company-reports', module, 'summary', from, to],
    queryFn: () => apiGet<ModuleReportSummary>(`/settings/company-reports/${module}/summary?${p.toString()}`),
    enabled: from !== '' && to !== '',
  })
}

export function moduleReportTableParams(r: ModuleReportTableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set('search[value]', r.search)
  p.set('filterFrom', r.filterFrom)
  p.set('filterTo', r.filterTo)
  if (r.severity !== undefined) p.set('severity', String(r.severity))
  return p
}

export function moduleReportTableQuery(module: ReportModule, table: string, r: ModuleReportTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'company-reports', module, 'table', table, r],
    queryFn: () =>
      apiGet<ModuleReportTablePage>(
        `/settings/company-reports/${module}/tables/${table}?${moduleReportTableParams(r).toString()}`,
      ),
    enabled: r.filterFrom !== '' && r.filterTo !== '',
  })
}
